import React, { useEffect, useState } from 'react'
import { Container, Form } from 'react-bootstrap'
import { useFormik } from 'formik'
import * as yup from 'yup'
import DashboardLayout from '../../../components/layouts/DashboardLayout'
import PageHeading from '../../../components/pageHeading'
import InputField from '../../../components/theme/InputField'
import ThemeButton from '../../../components/theme/ThemeButton'
import useFetch from '../../../hooks/useFetch'
import { showToast, notifyBugsnagError } from '../../../common/utils/Functions'
import styles from './help.module.scss'

const helpSchema = yup.object().shape({
    description: yup
        .string()
        .trim()
        .required("Please describe your problem or concern.")
        .max(500, "Description must be 500 characters or less"),
})

const Help = () => {
    const [contactMethods, setContactMethods] = useState({
        phone: false,
        email: false,
    })
    const [contactMethodError, setContactMethodError] = useState<string | null>(null)

    const [
        helpApi,
        { response: helpResponse, loading: helpLoading, error: helpError },
    ] = useFetch("/common/help/submit-request", {
        method: "post"
    })

    const submitHelpRequest = (values: { description: string }) => {
        // Map selected contact method to API enum: 0 => phone, 1 => email
        let contactPreference: number | null = null

        if (contactMethods.phone && !contactMethods.email) {
            contactPreference = 0
        } else if (!contactMethods.phone && contactMethods.email) {
            contactPreference = 1
        }

        try {
            helpApi({
                description: values.description,
                // 0 for phone, 1 for email
                contact_preferences: contactPreference!,
            })
        } catch (e: any) {
            notifyBugsnagError(e, {
                api: "helpApi",
            })
        }
    }

    const formik = useFormik({
        initialValues: {
            description: ""
        },
        validationSchema: helpSchema,
        onSubmit: submitHelpRequest,
    })

    const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()

        // Mark description field as touched to show validation error
        formik.setFieldTouched('description', true)

        // Validate description field
        const errors = await formik.validateForm()
        formik.setErrors(errors)

        // Validate contact method
        let contactPreference: number | null = null
        if (contactMethods.phone && !contactMethods.email) {
            contactPreference = 0
        } else if (!contactMethods.phone && contactMethods.email) {
            contactPreference = 1
        }

        if (contactPreference === null) {
            setContactMethodError("Please select one contact method.")
        } else {
            setContactMethodError(null)
        }

        // Only submit if both validations pass
        if (Object.keys(errors).length === 0 && contactPreference !== null) {
            submitHelpRequest(formik.values)
        }
    }

    useEffect(() => {
        if (helpResponse) {
            showToast(helpResponse.message as string || "Your help request has been submitted successfully", "success")
            formik.resetForm()
            setContactMethods({ phone: false, email: false })
            setContactMethodError(null)
        }
        if (helpError) {
            showToast(helpError.message as string, "error")
        }
    }, [helpResponse, helpError])

    const handleCheckboxChange = (method: 'phone' | 'email') => {
        // Clear error when user selects a checkbox
        setContactMethodError(null)

        setContactMethods(prev => {
            // Behave like radio buttons: only one contact method can be selected
            if (method === 'phone') {
                return {
                    phone: !prev.phone,
                    email: false,
                }
            }

            if (method === 'email') {
                return {
                    phone: false,
                    email: !prev.email,
                }
            }

            return prev
        })
    }

    return (
        <DashboardLayout>
            <Container>
                <PageHeading
                    heading="Help"
                    subHeading="We're here to help. Let us know what you need help with, and our support team will respond as soon as possible. Be sure your phone number is up-to-date if you've requested a phone call."
                />

                <div className={styles.helpFormCard}>
                    <Form onSubmit={handleFormSubmit}>
                        <div className={styles.formSection}>
                            <h5 className={styles.sectionHeading}>
                                Please briefly describe your problem or concern:
                            </h5>
                            <InputField
                                formik={formik}
                                name="description"
                                rows={4}
                                as='textarea'
                                placeholder="Describe your problem or concern here..."
                                className={styles.textareaField}
                                maxLength={500}
                            />
                        </div>

                        <div className={styles.formSection}>
                            <div className={styles.contactMethodSection}>
                                <h5 className={styles.sectionHeading}>
                                    Please contact me by:
                                </h5>
                                <div className={styles.checkboxGroup}>
                                    <Form.Check
                                        type="checkbox"
                                        id="contact-phone"
                                        checked={contactMethods.phone}
                                        onChange={() => handleCheckboxChange('phone')}
                                        label="Phone"
                                        className={styles.checkbox}
                                    />
                                    <Form.Check
                                        type="checkbox"
                                        id="contact-email"
                                        checked={contactMethods.email}
                                        onChange={() => handleCheckboxChange('email')}
                                        label="Email"
                                        className={styles.checkbox}
                                    />
                                </div>

                            </div>
                            {contactMethodError && (
                                <span className={styles.errorMessage}>
                                    {contactMethodError}
                                </span>
                            )}
                        </div>

                        <div className={styles.submitButtonWrapper}>
                            <ThemeButton
                                type="submit"
                                loading={helpLoading}
                                disabled={helpLoading}
                            >
                                Submit
                            </ThemeButton>
                        </div>
                    </Form>
                </div>
            </Container>
        </DashboardLayout>
    )
}

export default Help